
import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Plus, List, Settings2, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { preferencesSchema, TierFormData } from "@/contexts/program-creation/types";

type PreferencesCustomizationsFormProps = {
  onSubmit: (data: TierFormData) => void;
  onBack: () => void;
  isSaving: boolean;
};

export default function PreferencesCustomizationsForm({
  onSubmit,
  onBack,
  isSaving
}: PreferencesCustomizationsFormProps) {
  const [likedExercises, setLikedExercises] = useState<string[]>(['']);
  const [dislikedExercises, setDislikedExercises] = useState<string[]>(['']);

  const form = useForm<z.infer<typeof preferencesSchema>>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      periodizationType: "",
      periodizationDescription: "",
      likedExercises: [""],
      dislikedExercises: [""],
    },
  });

  const addLikedExercise = () => {
    setLikedExercises([...likedExercises, '']);
  };

  const addDislikedExercise = () => {
    setDislikedExercises([...dislikedExercises, '']);
  };

  const updateLikedExercise = (index: number, value: string) => {
    const updated = [...likedExercises];
    updated[index] = value;
    setLikedExercises(updated);
  };

  const updateDislikedExercise = (index: number, value: string) => {
    const updated = [...dislikedExercises];
    updated[index] = value;
    setDislikedExercises(updated);
  };

  const removeLikedExercise = (index: number) => {
    const updated = [...likedExercises];
    updated.splice(index, 1);
    // Ensure there's always at least one input field
    if (updated.length === 0) updated.push('');
    setLikedExercises(updated);
  };

  const removeDislikedExercise = (index: number) => {
    const updated = [...dislikedExercises];
    updated.splice(index, 1);
    // Ensure there's always at least one input field
    if (updated.length === 0) updated.push('');
    setDislikedExercises(updated);
  };

  const handleSubmit = () => {
    // Filter out empty exercises
    const filteredLikedExercises = likedExercises.filter(ex => ex.trim() !== '');
    const filteredDislikedExercises = dislikedExercises.filter(ex => ex.trim() !== '');
    
    const values = form.getValues();
    
    onSubmit({
      periodizationType: values.periodizationType,
      periodizationDescription: values.periodizationDescription,
      likedExercises: filteredLikedExercises,
      dislikedExercises: filteredDislikedExercises,
    });
  };

  return (
    <Card className="backdrop-blur-lg bg-white/10 border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Preferences & Customizations
        </CardTitle>
        <CardDescription className="text-white/70">
          Personal preferences and feedback settings to fine-tune the engagement strategy
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white/90">Periodization</h3>
              
              <FormField
                control={form.control}
                name="periodizationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/90 text-sm">
                      Periodization Type
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Linear, Undulating, Block, Conjugate"
                        className="bg-white/20 border-white/30 text-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="periodizationDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/90 text-sm">
                      Description (Optional)
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Additional details about your preferred periodization approach"
                        className="bg-white/20 border-white/30 text-white min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white/90 flex items-center gap-2">
                  <List className="h-5 w-5" />
                  Preferred Exercises
                </h3>
                
                <ScrollArea className="h-64 pr-4">
                  <div className="space-y-3">
                    {likedExercises.map((exercise, index) => (
                      <div key={`liked-${index}`} className="flex items-center gap-2">
                        <Input
                          value={exercise}
                          onChange={(e) => updateLikedExercise(index, e.target.value)}
                          placeholder="e.g., Barbell Squats"
                          className="bg-white/20 border-white/30 text-white"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLikedExercise(index)}
                          className="text-white/70 hover:text-white hover:bg-white/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 bg-white/10 hover:bg-white/20 text-white border-white/30"
                  onClick={addLikedExercise}
                  disabled={likedExercises.length >= 20} // Limit the number of exercises
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Exercise
                </Button>
              </div>
              
              <div className="space-y-4 border-l border-white/20 pl-6">
                <h3 className="text-lg font-medium text-white/90 flex items-center gap-2">
                  <List className="h-5 w-5" />
                  Exercises to Avoid
                </h3>
                
                <ScrollArea className="h-64 pr-4">
                  <div className="space-y-3">
                    {dislikedExercises.map((exercise, index) => (
                      <div key={`disliked-${index}`} className="flex items-center gap-2">
                        <Input
                          value={exercise}
                          onChange={(e) => updateDislikedExercise(index, e.target.value)}
                          placeholder="e.g., Leg Extensions"
                          className="bg-white/20 border-white/30 text-white"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDislikedExercise(index)}
                          className="text-white/70 hover:text-white hover:bg-white/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 bg-white/10 hover:bg-white/20 text-white border-white/30"
                  onClick={addDislikedExercise}
                  disabled={dislikedExercises.length >= 20} // Limit the number of exercises
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Exercise
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          className="bg-white/10 text-white hover:bg-white/20 border-white/30"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <Button 
          variant="purple"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Continue to Review'}
        </Button>
      </CardFooter>
    </Card>
  );
}
